import {
  ADD_SCHEDULES_IDP_SOCIAL_LEARNING,
  COMPLETE_SOCIAL_LEARNING,
  GET_DETAIL_EXPERIENTIAL_LEARNING,
  GET_DETAIL_SOCIAL_LEARNING,
  KICK_OFF_PROJECT_EXPERIENTIAL_LEARNING,
  SUBMIT_FEEDBACK_SUMMARY,
  UPDATE_COACHING_SUMMARY,
  UPLOAD_CERTIFICATE_IDP_LEARNING,
  UPLOAD_PRESENTATION_IDP_SOCIAL_LEARNING,
  UPLOAD_EVIDENCE_EXPERIENTIAL_LEARNING,
  SUBMIT_FEEDBACK_PROJECT_EXPERIENTIAL_LEARNING,
  ADD_PROGRESS_PROJECT_EXPERIENTIAL_LEARNING,
} from "@/api/idp/course-completion";
import {
  GET_DETAIL_STRUCTURED_LEARNING,
  GET_LIST_IDP_LEARNING,
  GET_SUMMARY_IDP_LEARNING,
  GET_IDP_PERIOD,
  GET_SUBORDINATES,
  GET_SUMMARY_IDP_SOCIAL_LEARNING,
} from "@/api/idp/enrollment";
import { GET_COMPETENCIES } from "@/api/master-data/competency/competency";
import { GET_TRAINING_METHOD } from "@/api/master-data/training-method/training-method";
import { GET_TRAINING_TYPE } from "@/api/master-data/training-type/training-type";

const handleRequest = async (apiFunc, commit, mutation, args, emptyValue = []) => {
  try {
    const resp = await apiFunc(args);
    const { httpCode, data } = resp.data;

    if (httpCode === 200) {
      if (data?.data && data?.totalItems !== undefined) {
        commit(mutation, {
          data: data.data,
          totalItems: data.totalItems,
          page: data.page,
          limit: data.limit,
          totalPages: data.totalPages,
        });
      } else {
        commit(mutation, data);
      }
      return data;
    }

    commit(mutation, emptyValue);
    return emptyValue;
  } catch (err) {
    const resp = err?.response?.data;
    if (resp?.httpCode === 404) {
      commit(mutation, emptyValue);
    }
    throw err;
  }
};

const state = {
  idpLearnings: {
    data: [],
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  },
  idpLearningReport: {
    data: [],
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  },
  structuredLearningDetail: null,
  socialLearningDetail: null,
  experientialLearningDetail: null,
  summaryIdpLearning: null,
  summaryIdpLearningReport: null,
  trainingMethods: [],
  trainingTypes: [],
  competencies: [],
  subordinates: [],
  subordinatesMeta: {
    totalItems: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  },
};

const mutations = {
  setIdpLearnings: (state, payload) => {
    state.idpLearnings = {
      data: payload.data ?? [],
      totalItems: payload.totalItems ?? 0,
      page: payload.page ?? 1,
      limit: payload.limit ?? 10,
      totalPages: payload.totalPages ?? 1,
    };
  },
  setIdpLearningReport: (state, payload) => {
    state.idpLearningReport = {
      data: payload.data ?? [],
      totalItems: payload.totalItems ?? 0,
      page: payload.page ?? 1,
      limit: payload.limit ?? 10,
      totalPages: payload.totalPages ?? 1,
    };
  },
  setStructuredLearningDetail: (state, payload) => (state.structuredLearningDetail = payload),
  setSocialLearningDetail: (state, payload) => (state.socialLearningDetail = payload),
  setExperientialLearningDetail: (state, payload) => (state.experientialLearningDetail = payload),
  summaryIdpLearning: (state, payload) => (state.summaryIdpLearning = payload),
  summaryIdpLearningReport: (state, payload) => (state.summaryIdpLearningReport = payload),
  setTrainingMethods: (state, payload) =>
    (state.trainingMethods = payload?.map((item) => ({ value: item.id, label: item.name })) || []),
  setTrainingTypes: (state, payload) =>
    (state.trainingTypes = payload?.map((item) => ({ value: item.id, label: item.name })) || []),
  setCompetencies: (state, payload) =>
    (state.competencies = payload?.data?.map((item) => ({ value: item.id, label: item.name })) || []),
  setSubordinates: (state, payload) => {
    const rows = Array.isArray(payload) ? payload : payload?.data || [];
    state.subordinates = rows.map((item) => ({ ...item, text: `${item.name} - ${item.nik}`, value: item.nik }));
    state.subordinatesMeta = {
      totalItems: Array.isArray(payload) ? rows.length : payload?.totalItems ?? rows.length,
      page: Array.isArray(payload) ? 1 : payload?.page ?? 1,
      limit: Array.isArray(payload) ? rows.length || 10 : payload?.limit ?? 10,
      totalPages: Array.isArray(payload) ? 1 : payload?.totalPages ?? 1,
    };
  },
  resetIdpLearnings: (state) => {
    state.idpLearnings = {
      data: [],
      total: 0,
      page: 1,
      limit: 10,
      totalPages: 1,
    };
  },
  resetIdpLearningReport: (state) => {
    state.idpLearningReport = {
      data: [],
      total: 0,
      page: 1,
      limit: 10,
      totalPages: 1,
    };
  },
  resetSummaryIdpLearning: (state) => (state.summaryIdpLearning = null),
  resetSummaryIdpLearningReport: (state) => (state.summaryIdpLearningReport = null),
};

const actions = {
  async getIdpLearnings({ commit }, query) {
    return handleRequest(GET_LIST_IDP_LEARNING, commit, "setIdpLearnings", query, []);
  },

  async getIdpLearningReport({ commit }, query) {
    return handleRequest(GET_LIST_IDP_LEARNING, commit, "setIdpLearningReport", query, []);
  },

  async getStructuredLearningDetail({ commit }, { id, query }) {
    return handleRequest(GET_DETAIL_STRUCTURED_LEARNING, commit, "setStructuredLearningDetail", { id, query }, null);
  },

  async getSocialLearningDetail({ commit }, { id, query }) {
    return handleRequest(GET_DETAIL_SOCIAL_LEARNING, commit, "setSocialLearningDetail", { id, query }, null);
  },

  async getExperientialLearningDetail({ commit }, { id, query }) {
    return handleRequest(GET_DETAIL_EXPERIENTIAL_LEARNING, commit, "setExperientialLearningDetail", { id, query }, null);
  },

  async uploadCertificateLearning(_, { id, data }) {
    return UPLOAD_CERTIFICATE_IDP_LEARNING(id, data);
  },

  async uploadPresentationLearning(_, { id, data }) {
    return UPLOAD_PRESENTATION_IDP_SOCIAL_LEARNING(id, data);
  },

  async uploadEvidenceLearning(_, { id, data }) {
    return UPLOAD_EVIDENCE_EXPERIENTIAL_LEARNING(id, data);
  },

  async addProgressProjectExperientialLearning(_, { id, data }) {
    return ADD_PROGRESS_PROJECT_EXPERIENTIAL_LEARNING(id, data);
  },

  async updateCoachingSummary(_, { id, data }) {
    return UPDATE_COACHING_SUMMARY(id, data);
  },

  async addCoachingSchedules(_, { id, data }) {
    return ADD_SCHEDULES_IDP_SOCIAL_LEARNING(id, data);
  },

  async submitFeedbackSummary(_, { id, data }) {
    return SUBMIT_FEEDBACK_SUMMARY(id, data);
  },

  async submitFeedbackProjectExperientialLearning(_, { id, data }) {
    return SUBMIT_FEEDBACK_PROJECT_EXPERIENTIAL_LEARNING(id, data);
  },

  async kickOffProjectExperientialLearning(_, { id, data }) {
    return KICK_OFF_PROJECT_EXPERIENTIAL_LEARNING(id, data);
  },

  async completeSocialLearning(_, { id }) {
    return COMPLETE_SOCIAL_LEARNING({ id });
  },

  async getSummaryIdpLearning({ commit }, query) {
    const resp = await handleRequest(GET_SUMMARY_IDP_LEARNING, commit, "summaryIdpLearning", query, null);
    return resp;
  },

  async getSummaryIdpSocialLearning({ commit }, query) {
    const resp = await handleRequest(GET_SUMMARY_IDP_SOCIAL_LEARNING, commit, "summaryIdpLearning", query, null);
    return resp;
  },

  async getSummaryIdpLearningReport({ commit }, query) {
    const resp = await handleRequest(GET_SUMMARY_IDP_SOCIAL_LEARNING, commit, "summaryIdpLearningReport", query, null);
    return resp;
  },

  async getIdpPeriod() {
    const response = await GET_IDP_PERIOD();
    return response.data.data;
  },

  async getTrainingMethods({ commit }, id) {
    return handleRequest(GET_TRAINING_METHOD, commit, "setTrainingMethods", id, null);
  },

  async getTrainingTypes({ commit }, id) {
    return handleRequest(GET_TRAINING_TYPE, commit, "setTrainingTypes", id, null);
  },

  async getCompetencies({ commit }, id) {
    return handleRequest(GET_COMPETENCIES, commit, "setCompetencies", id, null);
  },

  async getSubordinates({ commit }, query) {
    return handleRequest(GET_SUBORDINATES, commit, "setSubordinates", query, null);
  },

  resetIdpLearnings({ commit }) {
    commit("resetIdpLearnings");
    commit("resetSummaryIdpLearning");
    commit("resetSummaryIdpLearningReport");
    commit("resetIdpLearningReport");
  },
};

const getters = {
  idpLearnings: (state) => state.idpLearnings,
  structuredLearningDetail: (state) => state.structuredLearningDetail,
  socialLearningDetail: (state) => state.socialLearningDetail,
  experientialLearningDetail: (state) => state.experientialLearningDetail,
  summaryIdpLearning: (state) => state.summaryIdpLearning,
  trainingMethods: (state) => state.trainingMethods,
  trainingTypes: (state) => state.trainingTypes,
  competencies: (state) => state.competencies,
  subordinates: (state) => state.subordinates,
  subordinatesMeta: (state) => state.subordinatesMeta,
  idpLearningReport: (state) => state.idpLearningReport,
  summaryIdpLearningReport: (state) => state.summaryIdpLearningReport,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
